import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';
import { Icon } from "@iconify/react";

export default function AddVenue() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const bearerToken = localStorage.getItem('token');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [venueFeatures, setVenueFeatures] = useState([]);
  const [venueCities, setVenueCities] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const prevSlugRef = useRef('');
  const debounceTimer = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);


  const fetchFeatures = async () => {
    try {
      const response = await axios.get(`${apiUrl}event-service/venue-feature`);
      if (response.data?.success) {
        setVenueFeatures(response.data.data);
      } else throw new Error(response.data?.message || 'Error in fetching features');
    } catch (error) {
      console.error(error.response?.data?.message || error.message);
    }
  };

  const fetchVenueCities = async () => {
    try {
      const response = await axios.get(`${apiUrl}event-service/city`);
      if (response.data?.success) {
        setVenueCities(response.data.data);
      } else throw new Error(response.data?.message || 'Error in fetching cities');
    } catch (error) {
      console.error(error.response?.data?.message || error.message);
    }
  };

  useEffect(() => {
    fetchFeatures();
    fetchVenueCities();
  }, []);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    newVenue.setFieldValue("images", updatedImages);
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    newVenue.setFieldValue("images", updatedImages);
  };


  // Custom submit handler to show a toast for empty fields before running Yup validation,
  // while still allowing Formik to display field-specific errors and handle form submission.
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1️ Manual empty-field check for toast
    const emptyFields = Object.entries(newVenue.values)
      .filter(([_, value]) => !value)
      .map(([key]) => key);

    if (emptyFields.length > 0) {
      toast.error("Please fill all required fields!");
      // Still run Yup validation so errors show in form
      await newVenue.validateForm();
      // Mark all fields as touched so Formik shows the error messages
      newVenue.setTouched(
        Object.fromEntries(
          Object.keys(newVenue.values).map((key) => [key, true])
        )
      );
      return; // stop submission, but Yup errors are displayed
    }

    // 2️ No empty fields → continue with Yup validation
    const errors = await newVenue.validateForm();
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return;
    }

    // 3️ All good → trigger Formik onSubmit
    newVenue.handleSubmit();
  };





  const newVenue = useFormik({
    initialValues: {
      name: '',
      slug: '',
      location: '',
      cityId: '',
      address: '',
      ownerName: '',
      ownerPhone: '',
      ownerEmail: '',
      isSeat: true,
      features: [],
      lat: '',
      lng: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Required'),
      slug: Yup.string().required("Required"),
      cityId: Yup.string().required('Required'),
      address: Yup.string().required('Required'),
      ownerName: Yup.string().required('Required'),
      ownerPhone: Yup.string()
        .required("Required")
        .matches(/^\+94\d{9}$/, "Phone number must start with +94 and contain 9 digits after it")
        .length(12, "Phone number must be exactly 12 characters"),
      ownerEmail: Yup.string()
        .required('Required')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email')
        .email('Enter a valid email'),
      location: Yup.string()
        .matches(
          /^https:\/\/www\.google\.com\/maps\/embed\?pb=.*/i,
          'Please enter a valid embedded Google Maps URL'
        )
        .required('Location URL is required'),
      lat: Yup.number()
        .typeError('Latitude must be a decimal number')
        .required('Latitude is required')
        .min(-90, 'Latitude must be at least -90')
        .max(90, 'Latitude must be at most 90'),
      lng: Yup.number()
        .typeError('Longitude must be a decimal number')
        .required('Longitude is required')
        .min(-180, 'Longitude must be at least -180')
        .max(180, 'Longitude must be at most 180'),
      images: Yup.array()
        .nullable()
        .notRequired()
        .test("validImages", "Invalid image(s) in array", (value) => {
          if (!value) return true;
          return value.every(
            (img) =>
              typeof img === "string" || // For existing URLs
              (typeof img === "object" && img.file instanceof File)
          );
        })
        .test("fileSize", "Each file must be less than 1MB", (value) => {
          if (!value) return true;
          return value.every((img) => {
            if (typeof img === "string") return true;
            return img.file && img.file.size <= 1 * 1024 * 1024;
          });
        }),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      try {
        await validateSlug(values.slug, newVenue.setFieldError, newVenue.setFieldTouched);
        const formData = new FormData();
        Object.entries(values).forEach(([key, value]) => {
          if (key !== 'zones' && key !== 'features') {
            formData.append(key, value);
          }
        });
        formData.append("coordinate", JSON.stringify({
          lat: parseFloat(values.lat),
          lng: parseFloat(values.lng)
        }));
        formData.append("features", JSON.stringify(selectedFeatures));
        images.forEach((image) => {
          if (image.file) {
            formData.append("images", image.file);
          }
        });

        const response = await axios.post(`${apiUrl}event-service/event-venue`, formData, {
          headers: {
            'Authorization': `Bearer ${bearerToken}`,
            'Content-Type': 'multipart/form-data',
            'accept': 'application/json',
          },
        });

        if (response.data?.success) {
          resetForm();
          setTimeout(() => navigate('/venues'), 500);
          toast.success(response.data?.message || 'Venue successfully created!');
        } else {
          throw new Error('Failed to create venue');
        }
      } catch (error) {
        console.error('Error:', error.response?.data?.message || error.message);
        toast.error(error.response?.data?.message || error.message);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleFeaturesChange = (featureId) => {
    const updated = selectedFeatures.includes(featureId)
      ? selectedFeatures.filter(id => id !== featureId)
      : [...selectedFeatures, featureId];
    setSelectedFeatures(updated);
    newVenue.setFieldValue("features", updated);
  };

  const validateSlug = async (slug, setFieldError, setFieldTouched) => {
    if (!slug) return;
    setFieldTouched("slug", true);
    try {
      const response = await axios.get(`${apiUrl}event-service/event-venue/check-slug/${slug}`);
      if (!response.data.success) {
        setFieldError("slug", response.data.message || "Slug is not available");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setFieldError("slug", error.response.data.message || "Slug already taken");
      } else {
        setFieldError("slug", "Error validating slug. Please try again.");
      }
    }
  };

  useEffect(() => {
    const { name } = newVenue.values;
    if (name.trim()) {
      const slug = name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .trim();

      newVenue.setFieldValue('slug', slug);

      if (slug !== prevSlugRef.current) {
        prevSlugRef.current = slug;

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
          validateSlug(slug, newVenue.setFieldError, newVenue.setFieldTouched);
        }, 300);
      }
    }
  }, [newVenue.values.name]);

  return (
    <TailwindLayout>
      <TopBar />
      <div className="max-w-7xl mx-auto p-12 sm:p-12 lg:p-12 mb-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">

          <form onSubmit={handleSubmit} className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Venue</h2>

            <div className="grid md:grid-cols-2 gap-6 border-t pt-6">

              {/* Venue Info Column */}
              <div>
                <h3 className="text-lg font-medium mb-4">Venue Info</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="eg. The Grand Hall"
                    value={newVenue.values.name}
                    onChange={newVenue.handleChange}
                    onBlur={newVenue.handleBlur}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                  {newVenue.touched.name && newVenue.errors.name && <div className="text-red-500 text-sm">{newVenue.errors.name}</div>}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="name">
                    Unique Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    placeholder="eg. the-grand-Hall"
                    value={newVenue.values.slug}
                    onChange={newVenue.handleChange}
                    onBlur={() => validateSlug(newVenue.values.slug, newVenue.setFieldError, newVenue.setFieldTouched)}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                  {newVenue.touched.slug && newVenue.errors.slug && <div className="text-red-500 text-sm">{newVenue.errors.slug}</div>}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="location">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    placeholder="eg. https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.1234567890123!2d-122.4194156846814!3d37.7749292797598!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085808c8c8c8c8c%3A0x8c8c8c8c8c8c8c8c%2C+San+Francisco%2C+CA+94103%2C+USA!5e0!3m2!1sen!2sus!4v1234567890123"
                    value={newVenue.values.location}
                    onChange={newVenue.handleChange}
                    onBlur={newVenue.handleBlur}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                  {newVenue.touched.location && newVenue.errors.location && <div className="text-red-500 text-sm">{newVenue.errors.location}</div>}
                </div>
                <div className='mb-4'>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Latitude <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lat"
                        placeholder="eg. 37.7749"
                        value={newVenue.values.lat}
                        onChange={newVenue.handleChange}
                        onBlur={newVenue.handleBlur}
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                      />
                      {newVenue.touched.lat && newVenue.errors.lat && <div className="text-red-500 text-sm">{newVenue.errors.lat}</div>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Longitude <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lng"
                        placeholder="eg. 122.4194"
                        value={newVenue.values.lng}
                        onChange={newVenue.handleChange}
                        onBlur={newVenue.handleBlur}
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                      />
                      {newVenue.touched.lng && newVenue.errors.lng && <div className="text-red-500 text-sm">{newVenue.errors.lng}</div>}
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="city">
                    City <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="cityId"
                    value={newVenue.values.cityId}
                    onChange={newVenue.handleChange}
                    onBlur={newVenue.handleBlur}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  >
                    <option value="" disabled>
                      Select City
                    </option>
                    {venueCities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                  {newVenue.touched.cityId && newVenue.errors.cityId && <div className="text-red-500 text-sm">{newVenue.errors.cityId}</div>}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="address">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    placeholder="eg. 123 Main St, Colombo"
                    value={newVenue.values.address}
                    onChange={newVenue.handleChange}
                    onBlur={newVenue.handleBlur}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                  {newVenue.touched.address && newVenue.errors.address && <div className="text-red-500 text-sm">{newVenue.errors.address}</div>}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="featureId">
                    Venue Features
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {venueFeatures.length === 0 && <div className="text-gray-500 text-sm">No features available</div>}
                    {venueFeatures.map((feature) => (
                      <label key={feature.id} className="flex items-center space-x-1 p-0.5 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedFeatures && selectedFeatures.includes(feature.id)}
                          onChange={() => handleFeaturesChange(feature.id)}
                          className="h-3 w-3"
                        />
                        <span className="text-xs">{feature.name}</span>
                      </label>
                    ))}
                  </div>
                  {newVenue.touched.featureId && newVenue.errors.featureId && (
                    <div className="text-red-500 text-xs mt-1">{newVenue.errors.featureId}</div>
                  )}
                </div>
              </div>

              {/* Venue Owner Info Column */}
              <div>
                <h3 className="text-lg font-medium mb-4">Venue Owner Info</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="ownerName">
                    Owner Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ownerName"
                    placeholder="eg. John Doe"
                    value={newVenue.values.ownerName}
                    onChange={newVenue.handleChange}
                    onBlur={newVenue.handleBlur}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                  {newVenue.touched.ownerName && newVenue.errors.ownerName && <div className="text-red-500 text-sm">{newVenue.errors.ownerName}</div>}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="ownerPhone">
                    Owner Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="ownerPhone"
                    placeholder="eg. +94123456789"
                    value={newVenue.values.ownerPhone}
                    onChange={newVenue.handleChange}
                    onBlur={newVenue.handleBlur}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                  {newVenue.touched.ownerPhone && newVenue.errors.ownerPhone && <div className="text-red-500 text-sm">{newVenue.errors.ownerPhone}</div>}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" htmlFor="ownerEmail">
                    Owner Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="ownerEmail"
                    placeholder="eg. abc@zmail.com"
                    value={newVenue.values.ownerEmail}
                    onChange={newVenue.handleChange}
                    onBlur={newVenue.handleBlur}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                  />
                  {newVenue.touched.ownerEmail && newVenue.errors.ownerEmail && <div className="text-red-500 text-sm">{newVenue.errors.ownerEmail}</div>}
                </div>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-4">Venue Images <span className="text-gray-500 text-xs">(Recommended size: below 1mb, JPG/JPEG/PNG format)</span></h3>
              <div className="flex items-center">
                <label className="flex items-center text-sm font-medium px-4 py-2 text-blue-500 bg-blue-50 hover:bg-blue-200 rounded-lg cursor-pointer">
                  {/* <ImageIcon size={20} className="mr-2" /> */}
                  Upload Images
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    onBlur={() => newVenue.setFieldTouched("images", true)}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Image Preview */}
              <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-4">
                {images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={image.preview}
                      alt={`Venue ${index + 1}`}
                      className="w-full h-32 object-cover rounded-md cursor-pointer"
                      onClick={() => {
                        setSelectedImage(image.preview);
                        setIsPreviewOpen(true);
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <Icon icon="carbon:close-outline" className="sm" />
                    </button>
                  </div>
                ))}
              </div>
              {newVenue.touched.images && newVenue.errors.images && (
                <div className="text-red-500 text-sm">{newVenue.errors.images}</div>
              )}
            </div>
            {/* big image preview */}
            {isPreviewOpen && (
              <div
                className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
                onClick={() => setIsPreviewOpen(false)} // close when clicking background
              >
                <div className="relative">
                  <img
                    src={selectedImage}
                    alt="Preview"
                    className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-lg"
                  />

                  {/* Close button */}
                  <button
                    className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-md"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent closing when clicking button
                      setIsPreviewOpen(false);
                    }}
                  >
                    <Icon icon="carbon:close-outline" className="text-black w-6 h-6" />
                  </button>
                </div>
              </div>
            )}


            {/* Submit Button */}
            <div className="mt-6 flex items-center">
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              {isLoading && (
                <span className="ml-2 text-gray-600">Saving....</span>
              )}
              {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
                    <svg
                      className="animate-spin h-8 w-8 text-blue-600 mb-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>

                    <p className="text-sm font-medium text-gray-700">Saving...</p>
                  </div>
                </div>
              )}

            </div>
          </form>
        </div>
      </div>
      <FooterTailwind />
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </TailwindLayout>
  )
}